import { createAppSlice } from "../../../store/createAppSlice";
import { createSelector } from "@reduxjs/toolkit";
import type { PromptFormValues } from "../forms/PromptForm/PromptFormSchema";

export interface PromptConfiguration {
  id: string;
  values: PromptFormValues;
  promptVersionId?: string;
  promptRunId?: string;
}

export interface PromptHistoryState {
  isBrowsingHistory: Record<string, boolean>;
  inPreviewMode: Record<string, boolean>;
  selectedConfiguration: Record<string, PromptConfiguration | null>;
  restorableConfiguration: Record<string, PromptConfiguration | null>;
}

const initialState: PromptHistoryState = {
  isBrowsingHistory: {},
  inPreviewMode: {},
  selectedConfiguration: {},
  restorableConfiguration: {},
};

const promptHistorySlice = createAppSlice({
  name: "promptHistory",
  initialState,
  reducers: (create) => ({
    updateHistoryState: create.reducer<{
      promptId: string;
      isBrowsingHistory?: boolean;
      inPreviewMode?: boolean;
      selectedConfiguration?: PromptConfiguration | null;
      restorableConfiguration?: PromptConfiguration | null;
    }>((state, action) => {
      const {
        promptId,
        inPreviewMode,
        isBrowsingHistory,
        selectedConfiguration,
        restorableConfiguration,
      } = action.payload;

      if (isBrowsingHistory !== undefined) {
        state.isBrowsingHistory[promptId] = isBrowsingHistory;
      }

      if (inPreviewMode !== undefined) {
        state.inPreviewMode[promptId] = inPreviewMode;
      }

      if (selectedConfiguration) {
        // We need to deep copy the form data to avoid mutating the original object
        // in the react-hook-form. See: https://github.com/orgs/react-hook-form/discussions/3715
        const selectedConfigurationDeepCopy = JSON.parse(JSON.stringify(selectedConfiguration));
        state.selectedConfiguration[promptId] = selectedConfigurationDeepCopy;
      } else if (selectedConfiguration === null) {
        delete state.selectedConfiguration[promptId];
      }

      if (restorableConfiguration) {
        // We need to deep copy the form data to avoid mutating the original object
        // in the react-hook-form. See: https://github.com/orgs/react-hook-form/discussions/3715
        const restorableConfigurationDeepCopy = JSON.parse(JSON.stringify(restorableConfiguration));
        state.restorableConfiguration[promptId] = restorableConfigurationDeepCopy;
      } else if (restorableConfiguration === null) {
        delete state.restorableConfiguration[promptId];
      }
    }),

    // Reset form state when a tab is closed
    resetHistoryState: create.reducer<string[]>((state, action) => {
      const tabIds = action.payload;

      tabIds.forEach((promptId) => {
        delete state.isBrowsingHistory[promptId];
        delete state.inPreviewMode[promptId];
        delete state.selectedConfiguration[promptId];
        delete state.restorableConfiguration[promptId];
      });
    }),
  }),
  selectors: {
    // Get form state for a specific prompt
    getHistoryState: createSelector(
      [
        (state: PromptHistoryState) => state.isBrowsingHistory,
        (state: PromptHistoryState) => state.inPreviewMode,
        (state: PromptHistoryState) => state.selectedConfiguration,
        (state: PromptHistoryState) => state.restorableConfiguration,
        (_: PromptHistoryState, promptId: string) => promptId,
      ],
      (
        isBrowsingHistory,
        inPreviewMode,
        selectedConfiguration,
        restorableConfiguration,
        promptId,
      ) => ({
        isBrowsingHistory: isBrowsingHistory[promptId],
        inPreviewMode: inPreviewMode[promptId],
        selectedConfiguration: selectedConfiguration[promptId],
        restorableConfiguration: restorableConfiguration[promptId],
      }),
    ),
  },
});

const { actions, selectors } = promptHistorySlice;

export { actions, selectors, promptHistorySlice };
